import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FlexColumn,
  FlexRow,
  FilledButton,
  Rival,
  UserRank,
  useOnMountUnsafe,
  SvgIcon,
  OutlinedButton
} from '@make-software/tg-mini-app-ui';
import { useTheme } from 'styled-components';

import {
  PageWrapper,
  useMe,
  selectLeaderboardCount,
  selectLeaderboardData,
  selectUserRank,
  fetchLeaderboard,
  AppDispatch,
  useCollect,
  selectSeasonInfoRewardsCriteria,
  useSeasonInfo
} from '@/shared';
import { DISTRIBUTION_PATH, LEADERBOARD_PATH, RANKING_PATH } from '@/app/paths';
import { LeaderboardUser } from '@/entities';
import GoldIcon from '@/shared/assets/icons/golden-casper.svg';
import CheckmarkGreen from '@/shared/assets/icons/ic-checkmark-green.svg';
import RankCup from '@/shared/assets/icons/ic-rank-cup.svg';

import {
  InfoContainer,
  TokenSymbol,
  AwardText,
  StyledAwarded,
  LeaderBoardRow,
  EligibleUsersRow,
  ButtonWrapper
} from './ranking.styled';
import { AirdropEligibilityCriteria } from './components/airdrop-eligibility-criteria';
import { PrizeBadge } from './components/prize-badge';
import { getRankingPrizes } from './prizes';

export { LeaderBoardRow, EligibleUsersRow };

export const Ranking = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { collect } = useCollect();
  const currentUser = useMe().data!.user;
  const leaderboardData = useSelector(selectLeaderboardData);
  const myRank = useSelector(selectUserRank);
  const leaderboardCount = useSelector(selectLeaderboardCount);
  const { data: seasonInfoData, refresh: refreshRewardsCriteria } = useSeasonInfo();
  const rewardsCriteria = useSelector(selectSeasonInfoRewardsCriteria);
  const theme = useTheme();

  const rivals = leaderboardData.slice(0, 7) || [];
  const rankingPrizes = getRankingPrizes(rivals.map((r) => r.gets_rewards ?? false));
  const usersBelowPosition = Math.max((leaderboardCount || 0) - (myRank?.position || 0), 0);

  const navigate = useNavigate();

  const [isLoading] = useState<boolean>(false);

  useOnMountUnsafe(async () => {
    await collect({ url: RANKING_PATH });
    refreshRewardsCriteria();
    if (leaderboardData.length === 0) {
      dispatch(fetchLeaderboard({ offset: 0 }));
    }
  });

  const handleViewLeaderboard = () => {
    navigate(LEADERBOARD_PATH, {
      state: {
        currentUserId: currentUser?.id
      }
    });
  };

  const handleDistributionButton = async () => {
    await collect({ scene: 'drop', button_clicked: 'distribution_button' });
    navigate(DISTRIBUTION_PATH);
  };

  const getPrizeBadge = (idx: number): React.ReactNode | undefined => {
    const prize = rankingPrizes[idx];

    return prize !== undefined ? <PrizeBadge amount={prize} /> : undefined;
  };

  return (
    <PageWrapper>
      <>
        <InfoContainer gap={10}>
          <FlexRow gap={16} align={'center'}>
            <SvgIcon
              src={GoldIcon}
              width={30}
              height={30}
              alt="CSPR"
              fillColor={theme.colors.mainRed}
            />
            <AwardText monotype weight={1} size="xl">
              {seasonInfoData?.season.pool || '1,500,000'}
            </AwardText>
            <TokenSymbol monotype weight={3} size="xl">
              CSPR
            </TokenSymbol>
          </FlexRow>
          <FlexRow gap={8} align={'center'} style={{ marginTop: '8px' }}>
            <StyledAwarded>will be distributed based on the final rankings at the end of the Buildathon's final round (currently July 26th).</StyledAwarded>
          </FlexRow>
          <OutlinedButton
            title={'Learn about CSPR distribution'}
            onClick={handleDistributionButton}
            height={46}
            borderRadius="4px"
            padding="8px 16px"
            fontFamily={theme.typography.fontFamily.sans}
            fontSize={'12px'}
            fontWeight={500}
            style={{ marginTop: '16px' }}
          />
        </InfoContainer>

        <AirdropEligibilityCriteria
          tasks={rewardsCriteria.tasks}
          onchainActivity={rewardsCriteria.onchain_activity}
          balance={rewardsCriteria.balance}
          connectedWallet={rewardsCriteria.wallet}
        />

        {currentUser && (
          <>
            <LeaderBoardRow weight={1} size="lg">
              Your Position
            </LeaderBoardRow>
            <FlexColumn>
              <UserRank
                iconSrc={RankCup}
                iconColor={theme.colors.mainRed}
                title={`Rank ${myRank?.position || 0}/${leaderboardCount || 0}`}
                description={`You're doing better than ${usersBelowPosition} of other fans.`}
                border={'8px 8px 0 0'}
                background={'#30313B'}
              />
            </FlexColumn>
          </>
        )}
      </>

      <LeaderBoardRow weight={1} size="lg">
        Leaderboard
      </LeaderBoardRow>

      <FlexRow gap={10} direction={'column'}>
        {rivals.map((r: LeaderboardUser, i: number) => (
          <Rival
            rival={{
              username: r.username,
              points: r.points
            }}
            index={i + 1}
            key={r.user_id}
            itsMe={currentUser?.id === r.user_id}
            meetsRewardCriteria={r.gets_rewards ?? false}
            earnIconSrc={GoldIcon}
            earnIconColor={'#F1BF0B'}
            rewardIconSrc={CheckmarkGreen}
            defaultBorder="1px solid #41424E"
            defaultBackground="#30313B"
            itsMeBorder="1px solid #41424E"
            itsMeBackground="linear-gradient(90deg, rgba(251, 111, 106, 0.30) 0%, rgba(251, 111, 106, 0.00) 100%), #30313B"
            pointsFont={{
              fontFamily: theme.typography.fontFamily.display,
              color: theme.colors.contentPrimary,
              fontSize: '12px',
              fontWeight: 700
            }}
            borderRadius="8px"
            prizeBadge={getPrizeBadge(i)}
          />
        ))}
      </FlexRow>
      <ButtonWrapper>
        <FilledButton
          title={'View full Leaderboard'}
          onClick={handleViewLeaderboard}
          isLoading={isLoading}
          borderRadius="4px"
          fontWeight={500}
        />
      </ButtonWrapper>
    </PageWrapper>
  );
};
